import Vue from 'vue';
import { createAuth0Client } from '@auth0/auth0-spa-js';
import { apiPath } from '../authConfig';
import store from '../store';

/** Define a default action to perform after authentication */
const DEFAULT_REDIRECT_CALLBACK = () => {
  window.history.replaceState({}, document.title, window.location.pathname);
}

let authInstance;
/** Returns the current instance of the SDK */
export const getAuthInstance = () => authInstance;

/** Creates an instance of the Auth0 SDK. If one has already been created, it returns that instance */
export const useAuth0 = ({
  onRedirectCallback = DEFAULT_REDIRECT_CALLBACK,
  redirectUri = window.location.origin,
  ...options
}) => {
  if (authInstance) return authInstance;

  // The 'authInstance' is simply a Vue object
  authInstance = new Vue({
    data() {
      return {
        loading: true,
        isAuthenticated: false,
        user: {},
        auth0Client: null,
        error: null
      };
    },
    methods: {
      async storeUserAuth() {
        // update store user
        store.commit("auth/changeAuth", authInstance);
        store.commit("auth/setUserEmail", authInstance.user.email);
        store.commit("auth/setUserName", authInstance.user.name);
      },
      /** Handles the callback when logging in using a redirect */
      async handleRedirectCallback() {
        this.loading = true;
        try {
          await this.auth0Client.handleRedirectCallback();
          this.user = await this.auth0Client.getUser();
          this.isAuthenticated = true;
          this.error = null;
          this.accessToken = await this.auth0Client.getTokenSilently();
        } catch (e) {
          this.error = e;
        } finally {
          this.loading = false;
          // update store auth
          this.storeUserAuth();
        }
      },
      /** Authenticates the user using the redirect method */
      async loginWithRedirect(o) {
        return await this.auth0Client.loginWithRedirect(o);
      },
      /** Returns all the claims present in the ID token */
      getIdTokenClaims(o) {
        return this.auth0Client.getIdTokenClaims(o);
      },
      /** Returns the access token. If the token is invalid or missing, a new one is retrieved */
      async getTokenSilently(o) {
        return await this.auth0Client.getTokenSilently(o);
      },
      /** Logs the user out and removes their session on the authorization server */
      logout() {
        return this.auth0Client.logout({
          logoutParams: {
            returnTo: window.location.origin,
          }
        });
      },
      async initialize() {
        this.auth0Client = await createAuth0Client({
          ...options,
          authorizationParams: {
            audience: apiPath,
            redirect_uri: redirectUri,
            ...options.authorizationParams
          },
        });
  
        try {
          // If the user is returning to the app after authentication..
          if (
            window.location.search.includes('code=') &&
            window.location.search.includes('state=')
          ) {
            // handle the redirect and retrieve tokens
            const { appState } = await this.auth0Client.handleRedirectCallback();

            // Initialize our internal authentication state
            this.isAuthenticated = await this.auth0Client.isAuthenticated();
            this.user = await this.auth0Client.getUser();
            this.accessToken = await this.auth0Client.getTokenSilently();
            this.loading = false;
  
            this.error = null;

            // update store auth
            this.storeUserAuth();
  
            // Notify subscribers that the redirect callback has happened, passing the appState
            // (useful for retrieving any pre-authentication state)
            onRedirectCallback(appState);
          } else {
            // Initialize our internal authentication state
            this.isAuthenticated = await this.auth0Client.isAuthenticated();
            this.user = await this.auth0Client.getUser();
            this.accessToken = await this.auth0Client.getTokenSilently();
            this.loading = false;

            // update store auth
            this.storeUserAuth();
          }
          
        } catch (e) {
          this.error = e;
        }
      },
    },
  });

  return authInstance;
};

// Create a simple Vue plugin to expose the wrapper object throughout the application
export const Auth0Plugin = {
  install(Vue, options) {
    Vue.prototype.$auth = useAuth0(options);
  }
};